import { Preferences } from '@capacitor/preferences';
import { useCallback, useEffect, useRef, useState } from 'react';
import { normalizeCustomExercises } from './exercises';
import { IS_NATIVE_AUTH, nativeAuthHeaders, clearNativeAuth, startNativeLogin, listenForNativeLogin } from './nativeAuth';

async function readCache(key) {
  if (IS_NATIVE_AUTH) {
    const { value } = await Preferences.get({ key });
    if (value !== null) return value;
  }
  return localStorage.getItem(key);
}
function writeCache(key, value) {
  localStorage.setItem(key, value);
  if (IS_NATIVE_AUTH) Preferences.set({ key, value }).catch(() => {});
}
function removeCache(key) {
  localStorage.removeItem(key);
  if (IS_NATIVE_AUTH) Preferences.remove({ key }).catch(() => {});
}

const OWNER_KEY = 'xtimer:account-owner';
const PENDING_KEY = 'xtimer:account-pending';
const SYNC_KEY = 'xtimer:account-sync';
const EMPTY = { plans: [], trainingLog: [], customExercises: [], templates: [] };
const normalize = (data) => ({ ...EMPTY, ...data, customExercises: normalizeCustomExercises(data?.customExercises || []) });
function mergeGuest(remote, guest) {
  const unique = (entries) => [...new Map(entries.map((entry) => [JSON.stringify(entry), entry])).values()];
  return {
    plans: unique([...remote.plans, ...guest.plans]).slice(0, 200),
    trainingLog: [...new Map([...guest.trainingLog, ...remote.trainingLog].map((entry) => [entry.id, entry])).values()]
      .sort((a, b) => String(b.completedAt).localeCompare(String(a.completedAt))).slice(0, 200),
    customExercises: [...new Map([...guest.customExercises, ...remote.customExercises].map((entry) => [entry.name.toLowerCase(), entry])).values()],
    templates: unique([...remote.templates, ...guest.templates]).slice(0, 200)
  };
}

export function useAccountSync(apiBase, data, setters, librariesReady = true) {
  const [user, setUser] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [ready, setReady] = useState(false);
  const [status, setStatus] = useState('loading');
  const [retry, setRetry] = useState(0);
  const [hydrateRetry, setHydrateRetry] = useState(0);
  const latest = useRef(data);
  const applyRef = useRef(setters);
  const revision = useRef(0);
  const saved = useRef('');
  const userRef = useRef(null);
  const queue = useRef(Promise.resolve());
  const conflict = useRef(false);
  latest.current = data;
  applyRef.current = setters;
  const serialized = JSON.stringify(data);

  const request = useCallback(async (route, options = {}) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    let response;
    try { response = await fetch(`${apiBase}${route}`, {
      ...options, credentials: IS_NATIVE_AUTH ? 'omit' : 'include', headers: { 'Content-Type': 'application/json', ...await nativeAuthHeaders(), ...options.headers },
      signal: controller.signal
    }); } finally { clearTimeout(timeout); }
    if (!response.ok) {
      const error = new Error(`Request failed: ${response.status}`);
      error.status = response.status;
      throw error;
    }
    return response.status === 204 ? null : response.json();
  }, [apiBase]);
  const apply = useCallback((next) => {
    latest.current = next;
    for (const key of Object.keys(EMPTY)) applyRef.current[key](next[key]);
  }, []);

  const save = useCallback(() => {
    const job = queue.current.catch(() => {}).then(async () => {
      const activeUser = userRef.current;
      if (!activeUser || conflict.current) return;
      const payload = JSON.stringify(latest.current);
      if (payload === saved.current) return;
      setStatus('saving');
      writeCache(PENDING_KEY, JSON.stringify({ userId: activeUser.id, revision: revision.current, data: JSON.parse(payload) }));
      try {
        const result = await request('/api/account/data', {
          method: 'PUT', body: JSON.stringify({ revision: revision.current, data: JSON.parse(payload) })
        });
        revision.current = result.revision;
        saved.current = payload;
        writeCache(SYNC_KEY, JSON.stringify({ userId: activeUser.id, revision: result.revision, saved: payload }));
        if (JSON.stringify(latest.current) === payload) {
          removeCache(PENDING_KEY);
          setStatus('saved');
        } else {
          writeCache(PENDING_KEY, JSON.stringify({ userId: activeUser.id, revision: revision.current, data: latest.current }));
        }
      } catch (error) {
        conflict.current = error.status === 409;
        setStatus(error.status === 409 ? 'conflict' : error.status === 401 ? 'expired' : 'syncError');
        throw error;
      }
    });
    queue.current = job;
    return job;
  }, [request]);

  useEffect(() => {
    if (!librariesReady) return;
    setReady(false);
    let cancelled = false;
    (async () => {
      try {
        const auth = await request('/auth/me');
        if (cancelled) return;
        setEnabled(auth.enabled);
        if (!auth.user) { userRef.current = null; setUser(null); setStatus('local'); return; }
        const remote = await request('/api/account/data');
        if (cancelled) return;
        const remoteData = normalize(remote.data);
        revision.current = remote.revision;
        saved.current = JSON.stringify(remoteData);
        let next = remoteData;
        const owner = await readCache(OWNER_KEY);
        const pending = JSON.parse(await readCache(PENDING_KEY) || 'null');
        if (cancelled) return;
        conflict.current = false;
        if (pending?.userId === auth.user.id) {
          next = normalize(pending.data);
          if (pending.conflict || pending.revision !== remote.revision) {
            // A response may have been lost after a successful write.
            conflict.current = JSON.stringify(next) !== saved.current;
          }
        } else if (!owner) {
          next = mergeGuest(remoteData, normalize(latest.current));
        }
        writeCache(OWNER_KEY, auth.user.id);
        writeCache(SYNC_KEY, JSON.stringify({ userId: auth.user.id, revision: remote.revision, saved: saved.current }));
        apply(next);
        userRef.current = auth.user;
        setUser(auth.user);
        setStatus(conflict.current ? 'conflict' : 'saved');
        setReady(true);
      } catch (_error) { if (!cancelled) setStatus('syncError'); }
    })();
    return () => { cancelled = true; };
  }, [apply, request, hydrateRetry, librariesReady]);

  useEffect(() => {
    if (!ready || !user || serialized === saved.current) return;
    writeCache(PENDING_KEY, JSON.stringify({ userId: user.id, revision: revision.current, conflict: conflict.current, data: JSON.parse(serialized) }));
    if (conflict.current) return;
    setStatus('saving');
    const timeout = setTimeout(() => { save().catch(() => {}); }, 700);
    return () => clearTimeout(timeout);
  }, [ready, user, serialized, save, retry]);

  // Record edits even when the app starts offline or the login has expired.
  useEffect(() => {
    if (!librariesReady || ready || !['local', 'syncError'].includes(status)) return;
    let cancelled = false;
    (async () => {
      const owner = await readCache(OWNER_KEY);
      const metadata = JSON.parse(await readCache(SYNC_KEY) || 'null');
      if (cancelled || !owner || metadata?.userId !== owner || metadata.saved === serialized) return;
      const pending = JSON.parse(await readCache(PENDING_KEY) || 'null');
      if (!cancelled) writeCache(PENDING_KEY, JSON.stringify({ userId: owner,
        revision: pending?.userId === owner ? pending.revision : metadata.revision,
        conflict: pending?.conflict || false, data: JSON.parse(serialized) }));
    })().catch(() => {});
    return () => { cancelled = true; };
  }, [librariesReady, ready, status, serialized]);

  useEffect(() => {
    const online = () => setRetry((value) => value + 1);
    window.addEventListener('online', online);
    return () => window.removeEventListener('online', online);
  }, []);

  useEffect(() => {
    let cleanup;
    let cancelled = false;
    listenForNativeLogin(apiBase, () => setHydrateRetry((value) => value + 1), () => setStatus('loginError'))
      .then((remove) => { if (cancelled) remove(); else cleanup = remove; })
      .catch(() => setStatus('loginError'));
    return () => { cancelled = true; if (cleanup) cleanup(); };
  }, [apiBase]);

  const resolveConflict = async (keepLocal) => {
    try {
      await queue.current.catch(() => {});
      const remote = await request('/api/account/data');
      revision.current = remote.revision;
      saved.current = JSON.stringify(normalize(remote.data));
      writeCache(SYNC_KEY, JSON.stringify({ userId: userRef.current.id, revision: remote.revision, saved: saved.current }));
      conflict.current = false;
      if (!keepLocal) {
        apply(normalize(remote.data));
        removeCache(PENDING_KEY);
        setStatus('saved');
      } else {
        if (JSON.stringify(latest.current) === saved.current) {
          removeCache(PENDING_KEY);
          setStatus('saved');
        } else await save();
      }
    } catch (_error) { setStatus('syncError'); }
  };
  const logout = async () => {
    try {
      if (conflict.current) { setStatus('conflict'); return; }
      await save();
      await request('/auth/logout', { method: 'POST' });
      await clearNativeAuth();
      userRef.current = null;
      setUser(null);
      setReady(false);
      removeCache(OWNER_KEY);
      removeCache(PENDING_KEY);
      removeCache(SYNC_KEY);
      apply(EMPTY);
      setStatus('local');
      return true;
    } catch (error) { setStatus(error.status === 401 ? 'expired' : 'syncError'); return false; }
  };
  return {
    user, enabled, status, request, logout, resolveConflict,
    retry: () => ready ? setRetry((value) => value + 1) : setHydrateRetry((value) => value + 1),
    login: () => {
      setReady(false);
      userRef.current = null;
      return IS_NATIVE_AUTH ? startNativeLogin(apiBase).catch(() => setStatus('loginError')) : window.location.assign(`${apiBase}/auth/google`);
    }
  };
}
